import { useNavigate } from "react-router-dom";
import { faqCategories } from "../../data/faq";
import EditableText from "../ui/EditableText";

export interface FaqCategoryInput {
  title: string;
  image: string;
  path: string;
  summary: string;
}

const defaultCategories: FaqCategoryInput[] = faqCategories.map((category) => ({
  title: category.title,
  image: category.image,
  path: `/faq/${category.key}`,
  summary: category.summary,
}));

interface FaqCategoriesSectionProps {
  categories?: FaqCategoryInput[];
  editable?: boolean;
  onSaveTitle?: (index: number, value: string) => void;
  onSaveSummary?: (index: number, value: string) => void;
}

export default function FaqCategoriesSection({
  categories = defaultCategories,
  editable = false,
  onSaveTitle,
  onSaveSummary,
}: FaqCategoriesSectionProps) {
  const navigate = useNavigate();

  return (
    <div className="relative z-10 mx-auto mt-12 max-w-6xl">
      <div className="relative pt-12">
        <div className="absolute left-0 right-0 top-0">
          <div className="h-px w-full bg-white/70" />
          <div className="absolute left-0 top-0 h-4 w-px bg-white/70" />
          <div
            className="absolute top-0 h-4 w-px bg-white/70"
            style={{ left: "67.3%" }}
          />
          <div className="absolute right-0 top-0 h-4 w-px bg-white/70" />
        </div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {categories.map((category, index) => (
            <div key={index} className="relative">
              {index > 0 && (
                <div className="absolute left-[-1.25rem] top-0 hidden h-56 w-px bg-white/70 md:block" />
              )}

              <img
                src={category.image}
                alt={category.title}
                className="h-40 w-full border border-white object-cover"
              />

              {editable ? (
                <EditableText
                  as="h3"
                  value={category.title}
                  editMode
                  onSave={(value) => onSaveTitle?.(index, value)}
                  className="font-heading mt-6 block uppercase text-[20px] font-normal tracking-wide text-white"
                />
              ) : (
                <h3 className="font-heading mt-6 text-[20px] font-normal tracking-wide text-white">
                  {category.title.toUpperCase()}
                </h3>
              )}

              {editable ? (
                <EditableText
                  as="p"
                  value={category.summary}
                  editMode
                  multiline
                  onSave={(value) => onSaveSummary?.(index, value)}
                  className="mt-2 block text-sm font-light leading-relaxed text-slate-200"
                />
              ) : (
                <p className="mt-2 text-sm font-light leading-relaxed text-slate-200">
                  {category.summary}
                </p>
              )}

              <button
                onClick={() => navigate(category.path)}
                className="mt-4 block border border-white px-5 py-2 text-xs tracking-widest text-white hover:bg-white/10"
                aria-label={`Read more about ${category.title} FAQ`}
              >
                READ MORE
              </button>
            </div>
          ))}
        </div>

        <div className="relative mt-12">
          <div className="h-px w-full bg-white/70" />
          <div className="absolute bottom-0 left-0 h-4 w-px bg-white/70" />
          <div className="absolute bottom-0 right-0 h-4 w-px bg-white/70" />
        </div>
      </div>
    </div>
  );
}
